/**
 * Recommendations Hook
 *
 * Fetches recommendations from the BFF API using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, queryOptions } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import type { FundRating } from "@curie/shared";
import type {
  RecommendationCardType,
  RecommendationCardPriority,
} from "@/components/recommendations";

// ============================================================================
// Types
// ============================================================================

/**
 * Fund summary data from API
 */
export interface RecommendationFund {
  name: string;
  rating: FundRating;
  rank: number;
  totalFundsInCategory: number;
}

/**
 * Raw recommendation data from the API
 */
export interface RecommendationApiData {
  id: string;
  type: RecommendationCardType;
  priority: RecommendationCardPriority;
  reason: string;
  fromFundIsin?: string;
  toFundIsin?: string;
  currentValue?: number;
  taxImpact?: {
    stcg: number;
    ltcg: number;
    totalTax: number;
  };
  fromFund?: RecommendationFund;
  toFund?: RecommendationFund;
}

/**
 * Recommendations list response from API
 */
export interface RecommendationsListData {
  recommendations: RecommendationApiData[];
  count: number;
}

/**
 * Priority group counts for summary header
 */
export interface RecommendationCounts {
  immediate: number;
  recommended: number;
  optional: number;
  total: number;
}

/**
 * Grouped recommendations by priority
 */
export interface GroupedRecommendations {
  immediate: RecommendationApiData[];
  recommended: RecommendationApiData[];
  optional: RecommendationApiData[];
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for recommendations queries
 */
export const recommendationsKeys = {
  all: ["recommendations"] as const,
  lists: () => [...recommendationsKeys.all, "list"] as const,
  list: () => [...recommendationsKeys.lists()] as const,
  detail: (id: string) => [...recommendationsKeys.all, "detail", id] as const,
};

// ============================================================================
// Query Options
// ============================================================================

/**
 * Query options for recommendations list
 * Can be used with useQuery or prefetching
 */
export const recommendationsListQueryOptions = queryOptions({
  queryKey: recommendationsKeys.list(),
  queryFn: async (): Promise<RecommendationsListData> => {
    const response = await api.v1.recommendations.$get();
    return unwrapResponse<RecommendationsListData>(response);
  },
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 30, // 30 minutes
});

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Maps API priority to display group
 * - high -> immediate
 * - medium -> recommended
 * - low -> optional
 */
function mapPriorityToGroup(
  priority: RecommendationCardPriority
): keyof GroupedRecommendations {
  switch (priority) {
    case "high":
      return "immediate";
    case "medium":
      return "recommended";
    case "low":
      return "optional";
    default:
      return "optional";
  }
}

/**
 * Groups recommendations by priority
 */
export function groupRecommendationsByPriority(
  recommendations: RecommendationApiData[]
): GroupedRecommendations {
  const groups: GroupedRecommendations = {
    immediate: [],
    recommended: [],
    optional: [],
  };

  for (const rec of recommendations) {
    const group = mapPriorityToGroup(rec.priority);
    groups[group].push(rec);
  }

  return groups;
}

/**
 * Counts recommendations by priority group
 */
export function countRecommendations(
  recommendations: RecommendationApiData[]
): RecommendationCounts {
  const grouped = groupRecommendationsByPriority(recommendations);

  return {
    immediate: grouped.immediate.length,
    recommended: grouped.recommended.length,
    optional: grouped.optional.length,
    total: recommendations.length,
  };
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching recommendations with grouping utilities
 *
 * @example
 * ```tsx
 * function RecommendationsList() {
 *   const {
 *     data,
 *     groupedRecommendations,
 *     counts,
 *     isLoading,
 *     error,
 *     refetch
 *   } = useRecommendations();
 *
 *   if (isLoading) return <RecommendationsListSkeleton />;
 *   if (error) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       <p>{counts.immediate} Immediate</p>
 *       {groupedRecommendations.immediate.map((rec) => (
 *         <RecommendationCard key={rec.id} data={rec} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useRecommendations() {
  const query = useQuery(recommendationsListQueryOptions);

  // Compute grouped recommendations and counts from data
  const recommendations = query.data?.recommendations ?? [];
  const groupedRecommendations = groupRecommendationsByPriority(recommendations);
  const counts = countRecommendations(recommendations);

  return {
    ...query,
    recommendations,
    groupedRecommendations,
    counts,
  };
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isRecommendationsApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message for recommendations errors
 */
export function getRecommendationsErrorMessage(error: unknown): string {
  if (isRecommendationsApiError(error)) {
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    if (error.status === 404) {
      return "Recommendations not available.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading recommendations.";
}
